/*
 * <custom-minimal-collection>
 * Filter drawer open/close (right side), "Filter By +/−" toggle state,
 * Clear-all, Escape + backdrop close, focus handling. The filter form posts
 * natively (GET) so filtering works with no JS; this only enhances the UI.
 */
(function () {
  'use strict';

  class CustomMinimalCollection extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('[data-cmc-drawer]');
      this.toggles = Array.from(this.querySelectorAll('[data-cmc-filter-open]'));
      this.onKeydown = this.onKeydown.bind(this);

      this.toggles.forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleDrawer();
        })
      );

      if (this.drawer) {
        const closers = this.drawer.querySelectorAll('[data-cmc-drawer-close]');
        closers.forEach((el) => el.addEventListener('click', () => this.closeDrawer()));

        const backdrop = this.drawer.querySelector('[data-cmc-drawer-backdrop]');
        if (backdrop) backdrop.addEventListener('click', () => this.closeDrawer());

        const clear = this.drawer.querySelector('[data-cmc-clear]');
        if (clear) {
          clear.addEventListener('click', (e) => {
            // If JS is on, clear by navigating to the collection URL with no params.
            e.preventDefault();
            window.location.href = clear.getAttribute('href') || window.location.pathname;
          });
        }
      }

      document.addEventListener('keydown', this.onKeydown);

      this.initGalleries();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
    }

    /* Each multi-image card is a small gallery: chevrons (revealed on hover)
     * scrub through the product's images, and hovering previews the 2nd image
     * when "fade to second image on hover" is enabled — mirroring the product
     * page slider. Cards with a single image are left untouched. */
    initGalleries() {
      const hoverPreview = this.classList.contains('cmc--hover-image');
      this.querySelectorAll('.cmc-card--has-gallery').forEach((card) =>
        this.setupGallery(card, hoverPreview)
      );
    }

    setupGallery(card, hoverPreview) {
      const imgs = Array.from(card.querySelectorAll('.cmc-card__img'));
      if (imgs.length < 2) return;

      const prev = card.querySelector('[data-cmc-slider-prev]');
      const next = card.querySelector('[data-cmc-slider-next]');
      const total = imgs.length;
      let index = 0;
      let navigated = false;

      // Wrap around so clicking one direction walks through every image and
      // back to the start — no dead ends, every image is always reachable.
      const setActive = (i) => {
        index = ((i % total) + total) % total;
        imgs.forEach((img, n) => img.classList.toggle('is-active', n === index));
      };

      if (prev) {
        prev.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          navigated = true;
          setActive(index - 1);
        });
      }
      if (next) {
        next.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          navigated = true;
          setActive(index + 1);
        });
      }

      // Desktop: preview the 2nd image on hover, snap back to the cover on leave.
      card.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        if (hoverPreview && !navigated) setActive(1);
      });
      card.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        navigated = false;
        setActive(0);
      });

      setActive(0);
    }

    toggleDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.contains('is-open') ? this.closeDrawer() : this.openDrawer();
    }

    openDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.toggles.forEach((b) => b.setAttribute('aria-expanded', 'true'));
      document.documentElement.style.overflow = 'hidden';
      const focusTarget = this.drawer.querySelector('[data-cmc-drawer-close], a, button, input');
      if (focusTarget) window.requestAnimationFrame(() => focusTarget.focus());
    }

    closeDrawer() {
      if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.toggles.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      document.documentElement.style.overflow = '';
      if (this.toggles[0]) this.toggles[0].focus();
    }

    onKeydown(e) {
      if ((e.key === 'Escape' || e.key === 'Esc') && this.drawer && this.drawer.classList.contains('is-open')) {
        this.closeDrawer();
      }
    }
  }

  if (!customElements.get('custom-minimal-collection')) {
    customElements.define('custom-minimal-collection', CustomMinimalCollection);
  }
})();
